import React, { useState } from "react";
import { Alert, Box, Button, MenuItem, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";

import PageShell from "../layout/PageShell";
import { createHospital } from "../api/coreApi";
import { Colors } from "../../utils/Colors"; // ✅ import colors

const initial = {
  org_type: "HOSPITAL",
  name: "",
  registration_no: "",
  timezone: "Asia/Kolkata",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  country: "India",
  postal_code: "",
  phone: "",
};

export default function HospitalForm() {
  const nav = useNavigate();

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const onChange = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = async () => {
    setError("");
    setSuccess("");

    if (!form.name.trim()) return setError("Hospital name is required");
    if (!form.address_line1.trim()) return setError("Address line 1 is required");
    if (!form.city.trim()) return setError("City is required");
    if (!form.state.trim()) return setError("State is required");
    if (!form.postal_code.trim()) return setError("Postal code is required");

    setLoading(true);
    try {
      const res = await createHospital(form);
      const hospital = res.data;

      localStorage.setItem("hospital_id", hospital.hospital_id);

      setSuccess("Hospital saved ✅ Redirecting to Hospital List...");
      setForm(initial);

      nav("/core/lists", { replace: true });
    } catch (e) {
      setError(e?.response?.data ? JSON.stringify(e.response.data) : "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      title="Hospital / Organization Form"
      subtitle="Fill details and submit. Data will be saved to database."
    >
      {/* ALERTS */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            background: "#fff1f2",
            color: Colors.status.danger,
          }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            background: "#f0fdf4",
            color: Colors.status.success,
          }}
        >
          {success}
        </Alert>
      )}

      {/* FORM GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {/* COMMON TEXTFIELD STYLE */}
        {[
          {
            component: (
              <TextField
                select
                label="Org Type"
                value={form.org_type}
                onChange={onChange("org_type")}
              >
                <MenuItem value="HOSPITAL">Hospital</MenuItem>
                <MenuItem value="CLINIC">Clinic</MenuItem>
                <MenuItem value="LAB">Lab</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            ),
          },
          { component: <TextField label="Hospital Name" value={form.name} onChange={onChange("name")} /> },
          { component: <TextField label="Registration No" value={form.registration_no} onChange={onChange("registration_no")} /> },
          { component: <TextField label="Timezone" value={form.timezone} onChange={onChange("timezone")} /> },
          { component: <TextField label="Address Line 1" value={form.address_line1} onChange={onChange("address_line1")} /> },
          { component: <TextField label="Address Line 2" value={form.address_line2} onChange={onChange("address_line2")} /> },
          { component: <TextField label="City" value={form.city} onChange={onChange("city")} /> },
          { component: <TextField label="State" value={form.state} onChange={onChange("state")} /> },
          { component: <TextField label="Country" value={form.country} onChange={onChange("country")} /> },
          { component: <TextField label="Postal Code" value={form.postal_code} onChange={onChange("postal_code")} /> },
        ].map((item, i) => (
          <Box key={i}>
            {React.cloneElement(item.component, {
              fullWidth: true,
              sx: {
                "& .MuiOutlinedInput-root": {
                  background: Colors.bg.paper,
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: Colors.border.input,
                  },
                  "&:hover fieldset": {
                    borderColor: Colors.brand.primary,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: Colors.brand.primary,
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: Colors.text.muted,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: Colors.brand.primary,
                },
              },
            })}
          </Box>
        ))}

        {/* PHONE FULL WIDTH */}
        <TextField
          label="Phone"
          value={form.phone}
          onChange={onChange("phone")}
          fullWidth
          sx={{
            gridColumn: { xs: "1", md: "1 / span 2" },
            "& .MuiOutlinedInput-root": {
              background: Colors.bg.paper,
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {/* BUTTON */}
      <Box sx={{ mt: 4 }}>
        <Button
          variant="contained"
          onClick={submit}
          disabled={loading}
          sx={{
            px: 4,
            py: 1.2,
            fontWeight: 600,
            borderRadius: 2,
            background: Colors.brand.primary,
            boxShadow: Colors.shadow.brand,
            "&:hover": {
              background: Colors.brand.primaryDark,
            },
          }}
        >
          {loading ? "Saving..." : "Submit"}
        </Button>
      </Box>
    </PageShell>
  );
}